import React, { useEffect, useMemo, useState } from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import yml from 'js-yaml';

import CardSection from '../components/CardSection/CardSection';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';
import GettingStarted from '../components/GettingStarted/GettingStarted';
import GettingStartedModal from '../components/GettingsStartedModal/GettingStartedModal';
import { formatGettingStartedData } from '../components/GettingsStartedModal/useGettingStartedMap';
import GridCard from '../components/GridCard/GridCard';
import Layout from '../components/Layout';
import PageTitle from '../components/PageTitle/PageTitle';
import SEO from '../components/seo';
import { homePageCommunityCard, homePageDiscoverCard, homePageNavigation } from '../utils/constants';

import './index.scss';
import HomePageSidebar from '../components/SideBar/HomePageSidebar';

const gettingStartedsSw = ['ide-v1', 'ide-v2', 'web-editor'];

interface HomePageProps {
  families: {
    edges: Array<any>;
  };
  categories: {
    edges: Array<any>;
  };
  images: {
    edges: Array<any>;
  };
  products: {
    edges: Array<any>;
  };
  gettingstarted: {
    edges: Array<any>;
  };
  suggestions: {
    edges: Array<any>;
  };
  compatibilities: {
    edges: Array<any>;
  };
}

export default function Index(): React.ReactElement {
  const data: HomePageProps = useStaticQuery(pageQuery);

  // Background image
  // const bgImage = convertToBgImage(getImage(data.bgImage));
  const [openGettingStartedModal, setOpenGettingStartedModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedBoardCompatibility, setSelectedBoardCompatibility] = useState(null);
  const [chosenSoftware, setChosenSoftware] = useState(null);

  const gettingStartedProducts = useMemo(() => {
    return data.products.edges.filter((hw) => hw.node.fields && hw.node.fields.category === 'board');
  }, [data.products]);

  const selectOptions = useMemo(
    () =>
      gettingStartedProducts.map((p) => {
        return { value: p.node.fields.slug, label: p.node.childMdx.frontmatter.title };
      }),
    [gettingStartedProducts]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const board = urlParams.get('board');
    const sw = urlParams.get('sw');
    const selectedBoard = gettingStartedProducts.find((p) => p.node.fields.slug === `/hardware/${board}`);
    if (selectedBoard) {
      setSelectedBoardCompatibility(data.compatibilities.edges.find((c) => c.node.relativeDirectory === selectedBoard.node.relativeDirectory));
      setSelectedBoard(selectedBoard);
      if (gettingStartedsSw.find((s) => s === sw)) {
        setChosenSoftware(sw);
      }
      setOpenGettingStartedModal(true);
    }
  }, [data.compatibilities.edges, gettingStartedProducts]);

  return (
    <Layout>
      <SEO title="Arduino Docs" description="Browse through all our documentation to learn everything for your Arduino journey." />
      <main className="page-container">
        <HomePageSidebar links={homePageNavigation} />
        <div className="page-layout homepage">
          <PageTitle title="Arduino Documentation" description="Browse through all our documentation to learn everything for your Arduino journey." />
          <div className="card-grid">
            <GridCard
              title="Hardware"
              description="The vital pieces of hardware documentation you need to start your Arduino projects."
              linkText="BROWSE HARDWARE"
              link="/hardware"
              img="boards"
            />
            <GridCard
              title="Cloud"
              description="Arduino Cloud is a online platform that allows you to create, deploy and monitor IoT projects."
              linkText="DISCOVER CLOUD"
              link="/arduino-cloud"
              img="cloud"
            />
            <GridCard
              title="Software"
              description="Learn about the IDEs, Web Editor, CLI and all the software tools that you need to get your hands dirty."
              linkText="DISCOVER SOFTWARE"
              link="/software"
              img="ide"
            />
            <GridCard
              title="Programming"
              description="All you need to know to program with Arduino, including library documentation."
              linkText="START CODING"
              link="/programming"
              img="code"
            />
          </div>
          <GettingStarted
            setSelectedBoardCompatibility={(value) => setSelectedBoardCompatibility(value)}
            setSelectedBoard={(value) => setSelectedBoard(value)}
            setOpenGettingStartedModal={(value) => setOpenGettingStartedModal(value)}
            selectOptions={selectOptions}
            selectedBoard={selectedBoard}
            boardData={data}
            gettingStartedProducts={gettingStartedProducts}
          />
          <CardSection title="Discover" cardList={homePageDiscoverCard} />
          <CardSection title="Community" cardList={homePageCommunityCard} />
          <ConnectAndContribute />
        </div>

        {openGettingStartedModal && (
          <GettingStartedModal
            data={formatGettingStartedData(data)}
            isOpen={openGettingStartedModal}
            core={selectedBoard.node.childMdx.frontmatter.core}
            product={{
              name: selectedBoard.node.childMdx.frontmatter.title,
              slug: selectedBoard.node.fields.slug,
              compatibility: yml.load(selectedBoardCompatibility?.node.fields.content),
            }}
            onClose={() => setOpenGettingStartedModal(false)}
            chosenSoftware={chosenSoftware}
            suggestions={data.suggestions.edges.find((b) => b.node.relativeDirectory === selectedBoard.node.relativeDirectory)?.node}
          />
        )}
      </main>
    </Layout>
  );
}

export const pageQuery = graphql`
  {
    families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
              description
            }
          }
        }
      }
    }
    categories: allFile(filter: { base: { eq: "category.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
            }
          }
        }
      }
    }
    products: allFile(filter: { base: { eq: "product.md" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            slug
            category
          }
          childMdx {
            frontmatter {
              title
              core
            }
          }
        }
      }
    }
    compatibilities: allFile(filter: { base: { eq: "compatibility.yml" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            content
          }
        }
      }
    }
    suggestions: allFile(filter: { base: { eq: "suggestions.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            body
          }
        }
      }
    }
    gettingstarted: allFile(filter: { fields: { type: { eq: "softwarepage" }, gettingstarted: { ne: null } } }, sort: { fields: { slug: ASC } }) {
      edges {
        node {
          fields {
            slug
            gettingstarted
            software
            core
          }
          childMdx {
            body
            tableOfContents
            frontmatter {
              title
              description
            }
          }
        }
      }
    }
    images: allFile(filter: { base: { eq: "image.png" } }) {
      edges {
        node {
          relativeDirectory
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
